import type { SortingState } from "@salestrak/ui";
import type { SortDirection } from "#app/lib/data";

/**
 * Combine multiple where conditions with AND logic.
 * Returns undefined if no conditions, the single condition if only one,
 * or an AND wrapper for multiple.
 */
export function combineWhereConditions(
  conditions: Record<string, unknown>[]
): Record<string, unknown> | undefined {
  if (conditions.length === 0) return undefined;
  if (conditions.length === 1) return conditions[0];
  return { AND: conditions };
}

export function applyCurrentUserFilter(
  where: Record<string, unknown> | undefined,
  isCurrentUserFilterApplied: boolean
): Record<string, unknown> {
  if (!isCurrentUserFilterApplied) return where ?? {};
  return { ...(where ?? {}), onlyCurrentUser: true };
}

/**
 * Build a search where condition for the given fields.
 * Returns undefined if the search string is empty.
 */
export function buildSearchWhere(
  search: string,
  fields: readonly string[]
): Record<string, unknown> | undefined {
  if (!search) return undefined;
  return {
    OR: fields.map((field) => ({
      [field]: { contains: search, mode: "insensitive" },
    })),
  };
}

/**
 * Convert TanStack Table sorting state to a GraphQL orderBy object.
 */
export function buildOrderBy(
  sorting: SortingState,
  defaultField: string,
  defaultDirection: SortDirection = "asc"
): Record<string, SortDirection> {
  const [primary] = sorting;
  if (!primary) return { [defaultField]: defaultDirection };
  return { [primary.id]: primary.desc ? "desc" : "asc" };
}
